<template>
    <div class="mail-editor">
        <div class="editor-toolbar">
            <div class="toolbar-group">
                <!-- A butonu - tıklandığında format araçlarını gösterecek -->
                <a-tooltip placement="top" :title="$t('mail.stringFormats')">
                    <SupsisButton
                        xxsmall
                        @click="toggleFormatTools"
                        :primary="showFormatTools"
                        classNames="square-button text-button"
                    >
                        A
                    </SupsisButton>
                </a-tooltip>

                <!-- Format araçları -->
                <div v-if="showFormatTools" class="format-tools">
                    <div class="format-section">
                        <a-tooltip placement="top" :title="$t('mail.bold')">
                            <SupsisButton
                                icon="format_bold"
                                :class="{ active: isEditorActive('bold') }"
                                :primary="isEditorActive('bold')"
                                @click="handleEditorCommand('bold')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('mail.italic')">
                            <SupsisButton
                                icon="format_italic"
                                :class="{ active: isEditorActive('italic') }"
                                :primary="isEditorActive('italic')"
                                @click="handleEditorCommand('italic')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('mail.underline')">
                            <SupsisButton
                                icon="format_underlined"
                                :class="{ active: isEditorActive('underline') }"
                                :primary="isEditorActive('underline')"
                                @click="handleEditorCommand('underline')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                    </div>
                    <div class="divider-vertical"></div>
                    <div class="format-section">
                        <a-tooltip placement="top" :title="$t('mail.fontFamily')">
                            <a-dropdown :trigger="['click']" placement="bottomLeft">
                                <SupsisButton icon="text_fields" xxsmall classNames="square-button" />
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item @click="handleEditorCommand('paragraph')">
                                           {{ $t("mail.normalText") }}
                                        </a-menu-item>
                                        <a-menu-item @click="handleEditorCommand('heading', { level: 1 })">
                                            {{ $t("message.title") }} 1
                                        </a-menu-item>
                                        <a-menu-item @click="handleEditorCommand('heading', { level: 2 })">
                                            {{ $t("message.title") }} 2
                                        </a-menu-item>
                                        <a-menu-item @click="handleEditorCommand('heading', { level: 3 })">
                                            {{ $t("message.title") }} 3
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('mail.textColor')">
                            <a-dropdown :trigger="['click']" placement="bottomLeft">
                                <SupsisButton icon="format_color_text" xxsmall classNames="square-button" />
                                <template #overlay>
                                    <div class="color-picker">
                                        <div class="color-options">
                                            <div
                                                v-for="color in colorOptions"
                                                :key="color.value"
                                                class="color-option"
                                                :style="{ backgroundColor: color.value }"
                                                @click="setTextColor(color.value)"
                                                :title="color.name"
                                            ></div>
                                        </div>
                                    </div>
                                </template>
                            </a-dropdown>
                        </a-tooltip>
                    </div>
                    <div class="divider-vertical"></div>
                    <div class="format-section">
                        <a-tooltip placement="top" :title="$t('mail.alignLeft')">
                            <SupsisButton
                                icon="format_align_left"
                                :class="{ active: isEditorActive({ textAlign: 'left' }) }"
                                :primary="isEditorActive({ textAlign: 'left' })"
                                @click="handleEditorCommand('textAlign', 'left')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('mail.alignCenter')">
                            <SupsisButton
                                icon="format_align_center"
                                :class="{ active: isEditorActive({ textAlign: 'center' }) }"
                                :primary="isEditorActive({ textAlign: 'center' })"
                                @click="handleEditorCommand('textAlign', 'center')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                        <a-tooltip placement="top" :title="$t('mail.alignRight')">
                            <SupsisButton
                                icon="format_align_right"
                                :class="{ active: isEditorActive({ textAlign: 'right' }) }"
                                :primary="isEditorActive({ textAlign: 'right' })"
                                @click="handleEditorCommand('textAlign', 'right')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-tooltip>
                    </div>
                </div>

                <span class="divider"></span>

                <!-- Diğer araçlar - A butonu aktifse gizle -->
                <div v-if="!showFormatTools" class="other-tools">
                    <!-- Resim ekleme butonu -->
                    <a-tooltip placement="top" :title="'Dosya Yükle'" v-if="!inlineTextOnly">
                        <SupsisButton icon="image" @click="$emit('image-upload')" xxsmall classNames="square-button" />
                    </a-tooltip>

                    <span class="divider" v-if="!inlineTextOnly"></span>

                    <!-- Link butonu - sadece metin seçildiğinde göster -->
                    <a-tooltip placement="top" :title="$t('message.addLink')" v-if="editor && !editor.state.selection.empty">
                        <a-popover
                            v-model:visible="showLinkPopover"
                            placement="bottom"
                            trigger="click"
                            overlayClassName="link-popover-container"
                        >
                            <template #content>
                                <div class="link-form">
                                    <div class="form-group">
                                        <a-input
                                            v-model:value="linkUrl"
                                            placeholder="https://"
                                            @keydown.enter="applyLink"
                                        />
                                    </div>
                                    <div class="form-group form-actions">
                                        <div class="action-buttons">
                                            <SupsisButton @click="hideLinkPopover" classNames="form-button">
                                                {{ $t("mail.cancel") }}
                                            </SupsisButton>
                                            <SupsisButton
                                                v-if="isEditorActive('link')"
                                                danger
                                                @click="removeLink"
                                                classNames="form-button"
                                            >
                                                {{ $t("settings.remove") }}
                                            </SupsisButton>
                                            <SupsisButton primary @click="applyLink" classNames="form-button">
                                                {{ $t("ticimax.apply") }}
                                            </SupsisButton>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <SupsisButton
                                icon="link"
                                :class="{ active: isEditorActive('link') }"
                                :primary="isEditorActive('link')"
                                xxsmall
                                classNames="square-button"
                            />
                        </a-popover>
                    </a-tooltip>

                    <!-- Emoji butonu -->
                    <a-tooltip placement="top" :title="$t('mail.addEmoji')">
                        <EmojiButton :on-emoji-picked="addEmoji">
                            <template #button>
                                <SupsisButton icon="mood" xxsmall classNames="square-button" />
                            </template>
                        </EmojiButton>
                    </a-tooltip>

                    <!-- AI asistanı butonu - hideAIAssistant prop'una göre göster/gizle -->
                    <a-tooltip placement="top" :title="$t('plans.aiAssistant')" v-if="!hideAIAssistant">
                        <SupsisButton icon="psychology" @click="showAIAssistant" xxsmall classNames="square-button" />
                    </a-tooltip>
                </div>

                <div class="toolbar-spacer"></div>

                <!-- Geri/İleri butonları -->
                <a-tooltip placement="top" :title="$t('mail.takeItBack')">
                    <SupsisButton icon="undo" @click="handleEditorCommand('undo')" xxsmall classNames="square-button" />
                </a-tooltip>
                <a-tooltip placement="top" :title="$t('mail.fastForward')">
                    <SupsisButton icon="redo" @click="handleEditorCommand('redo')" xxsmall classNames="square-button" />
                </a-tooltip>
            </div>
        </div>

        <editor-content v-if="editor" :editor="editor" class="tiptap-editor" />

        <!-- Seçili metin üzerinde çıkacak hızlı araç çubuğu -->
        <div v-if="showFloatingToolbar" class="floating-toolbar" :style="floatingToolbarStyle">
            <a-tooltip placement="top" :title="$t('mail.bold')">
                <SupsisButton
                    icon="format_bold"
                    :class="{ active: isEditorActive('bold') }"
                    :primary="isEditorActive('bold')"
                    @click="handleEditorCommand('bold')"
                    xxsmall
                    classNames="square-button"
                />
            </a-tooltip>
            <a-tooltip placement="top" :title="$t('mail.italic')">
                <SupsisButton
                    icon="format_italic"
                    :class="{ active: isEditorActive('italic') }"
                    :primary="isEditorActive('italic')"
                    @click="handleEditorCommand('italic')"
                    xxsmall
                    classNames="square-button"
                />
            </a-tooltip>
            <a-tooltip placement="top" :title="$t('mail.underline')">
                <SupsisButton
                    icon="format_underlined"
                    :class="{ active: isEditorActive('underline') }"
                    :primary="isEditorActive('underline')"
                    @click="handleEditorCommand('underline')"
                    xxsmall
                    classNames="square-button"
                />
            </a-tooltip>
            <a-tooltip placement="top" :title="$t('plans.aiAssistant')" v-if="!hideAIAssistant">
                <SupsisButton icon="psychology" @click="showAIAssistant" xxsmall classNames="square-button" />
            </a-tooltip>
        </div>

        <!-- Tooltip -->
        <div v-if="tooltipVisible" class="toolbar-tooltip" :style="tooltipStyle">
            {{ tooltipText }}
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import EmojiButton from "../../../../../components/EmojiButton.vue";
import { CollapsedMail } from "./extensions/CollapsedMailExtension";

export default {
    name: "MailEditor",
    components: {
        EditorContent,
        SupsisButton,
        EmojiButton,
    },
    props: {
        value: {
            type: String,
            default: "",
        },
        hideAIAssistant: {
            type: Boolean,
            default: false,
        },
        inlineTextOnly: {
            type: Boolean,
            default: false,
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editor: null,
            showFloatingToolbar: false,
            showFormatTools: false, // A butonuna tıklanıp tıklanmadığını kontrol eder
            floatingToolbarStyle: {
                top: "0px",
                left: "0px",
            },
            showLinkPopover: false,
            showColorPopover: false,
            colorPopoverStyle: {
                top: "0px",
                left: "0px",
            },
            linkPopoverStyle: {
                top: "0px",
                left: "0px",
            },
            linkUrl: "",
            linkText: "",
            tooltipVisible: false,
            tooltipText: "",
            tooltipStyle: {
                top: "0px",
                left: "0px",
            },
            tooltipTimeout: null,
            colorOptions: [
                { name: "Siyah", value: "#000000" },
                { name: "Gri", value: "#888888" },
                { name: "Kırmızı", value: "#ff0000" },
                { name: "Turuncu", value: "#ff8800" },
                { name: "Sarı", value: "#ffcc00" },
                { name: "Yeşil", value: "#00cc00" },
                { name: "Turkuaz", value: "#00cccc" },
                { name: "Mavi", value: "#0066ff" },
                { name: "Mor", value: "#8800ff" },
                { name: "Pembe", value: "#ff00cc" },
            ],
            cancels: [], // İnterval ve listener referansları için dizi
        };
    },
    computed: {
        currentHeadingText() {
            if (!this.editor) return "Normal metin";

            if (this.editor.isActive("heading", { level: 1 })) return "Başlık 1";
            if (this.editor.isActive("heading", { level: 2 })) return "Başlık 2";
            if (this.editor.isActive("heading", { level: 3 })) return "Başlık 3";

            return "Normal metin";
        },
    },
    mounted() {
        this.initEditor();

        // Event listener ekle ve temizleme fonksiyonunu cancels dizisine ekle
        const selectionChangeHandler = this.handleSelectionChange.bind(this);
        document.addEventListener("selectionchange", selectionChangeHandler);
        this.cancels.push(() => document.removeEventListener("selectionchange", selectionChangeHandler));

        const outsideClickHandler = this.handleOutsideClick.bind(this);
        document.addEventListener("click", outsideClickHandler);
        this.cancels.push(() => document.removeEventListener("click", outsideClickHandler));

        // Tooltip timeout için temizleme fonksiyonu
        this.cancels.push(() => {
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
            }
        });
    },
    beforeDestroy() {
        // Editor'ü temizle
        if (this.editor) {
            this.editor.destroy();
        }

        // Tüm interval ve listenerları temizle
        this.cancels.forEach((cancel) => cancel());
    },
    methods: {
        // A butonuna tıklanınca format araçlarını göster/gizle
        toggleFormatTools() {
            this.showFormatTools = !this.showFormatTools;
        },
        initEditor() {
            this.editor = new Editor({
                extensions: [
                    StarterKit,
                    Underline,
                    Link.configure({
                        openOnClick: false,
                        linkOnPaste: true,
                    }),
                    TextAlign.configure({
                        types: ["heading", "paragraph"],
                    }),
                    Image.configure({
                        inline: this.inlineTextOnly, // inline özelliğini inlineTextOnly prop'una göre ayarla
                    }),
                    TextStyle,
                    Color,
                    CollapsedMail, // Özel forward mail uzantısını ekle
                ],
                content: this.value,
                onUpdate: ({ editor }) => {
                    this.$emit("input", editor.getHTML());
                },
                autofocus: this.autoFocus,
                editable: true,
                // HTML etiketlerini doğrudan kullanma izni verelim
                enablePasteRules: false,
                enableInputRules: false,
            });
        },
        isEditorActive(type) {
            if (!this.editor) return false;
            return this.editor.isActive(type);
        },
        handleEditorCommand(command, attrs) {
            if (!this.editor) return;

            switch (command) {
                case "bold":
                    this.editor.chain().focus().toggleBold().run();
                    break;
                case "italic":
                    this.editor.chain().focus().toggleItalic().run();
                    break;
                case "underline":
                    this.editor.chain().focus().toggleUnderline().run();
                    break;
                case "strike":
                    this.editor.chain().focus().toggleStrike().run();
                    break;
                case "bulletList":
                    this.editor.chain().focus().toggleBulletList().run();
                    break;
                case "orderedList":
                    this.editor.chain().focus().toggleOrderedList().run();
                    break;
                case "blockquote":
                    this.editor.chain().focus().toggleBlockquote().run();
                    break;
                case "codeBlock":
                    this.editor.chain().focus().toggleCodeBlock().run();
                    break;
                case "indent":
                    this.editor.chain().focus().indent().run();
                    break;
                case "outdent":
                    this.editor.chain().focus().outdent().run();
                    break;
                case "heading":
                    this.editor.chain().focus().toggleHeading(attrs).run();
                    break;
                case "paragraph":
                    this.editor.chain().focus().setParagraph().run();
                    break;
                case "textAlign":
                    this.editor.chain().focus().setTextAlign(attrs).run();
                    break;
                case "undo":
                    this.editor.chain().focus().undo().run();
                    break;
                case "redo":
                    this.editor.chain().focus().redo().run();
                    break;
            }
        },
        makeShowLinkPopover() {
            if (this.editor.isActive("link")) {
                this.linkUrl = this.editor.getAttributes("link").href || "";
            } else {
                this.linkUrl = "";
            }

            const { state } = this.editor;
            const { from, to } = state.selection;
            this.linkText = state.doc.textBetween(from, to, "");

            // Popover görünürlüğünü doğrudan açıyoruz
            this.showLinkPopover = true;

            // Input'a focus yapalım
            setTimeout(() => {
                if (this.$el.querySelector(".link-form .form-group input")) {
                    this.$el.querySelector(".link-form .form-group input").focus();
                }
            }, 10);
        },
        hideLinkPopover() {
            this.showLinkPopover = false;
        },
        applyLink() {
            if (!this.linkUrl) {
                this.removeLink();
                return;
            }

            // URL geçerli değilse düzelt
            let url = this.linkUrl;
            if (!/^https?:\/\//.test(url)) {
                url = "https://" + url;
            }

            // Metin seçili değilse ve linkText var ise
            if (this.linkText && this.editor.state.selection.empty) {
                this.editor
                    .chain()
                    .focus()
                    .insertContent(this.linkText)
                    .setTextSelection({
                        from: this.editor.state.selection.from - this.linkText.length,
                        to: this.editor.state.selection.from,
                    })
                    .setLink({
                        href: url,
                        target: "_blank", // Varsayılan olarak yeni pencerede açılması için
                    })
                    .run();
            } else {
                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange("link")
                    .setLink({
                        href: url,
                        target: "_blank", // Varsayılan olarak yeni pencerede açılması için
                    })
                    .run();
            }

            this.hideLinkPopover();
        },
        removeLink() {
            this.editor.chain().focus().unsetLink().run();
            this.hideLinkPopover();
        },
        showAIAssistant() {
            // Sadece event emit edelim, modal oluşturulmasın
            this.$emit("ai-assistant");
        },
        handleSelectionChange() {
            if (!this.editor) return;

            // Seçili metin varsa ve editorün içindeyse floating toolbar göster
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && !selection.isCollapsed) {
                const range = selection.getRangeAt(0);
                const editorElement = this.$el.querySelector(".tiptap-editor");

                if (editorElement && editorElement.contains(range.commonAncestorContainer)) {
                    const rect = range.getBoundingClientRect();
                    const editorRect = editorElement.getBoundingClientRect();

                    this.showFloatingToolbar = true;
                    this.floatingToolbarStyle = {
                        top: `${rect.top - editorRect.top - 30}px`,
                        left: `${rect.left + rect.width / 2 - 40}px`,
                    };
                }
            } else {
                this.showFloatingToolbar = false;
            }

            // Link popover'ı otomatik olarak kapat
            if (this.showLinkPopover && this.editor.state.selection.empty) {
                this.hideLinkPopover();
            }
        },
        handleOutsideClick(event) {
            // Link popover artık a-popover ile yönetildiği için bu kısma gerek yok
            // Sadece floating toolbar'ı kontrol edelim
            if (this.showFloatingToolbar && !this.$el.querySelector(".floating-toolbar").contains(event.target)) {
                this.showFloatingToolbar = false;
            }
        },
        showTooltip(type, event) {
            clearTimeout(this.tooltipTimeout);

            const tooltips = {
                bold: "Kalın",
                italic: "İtalik",
                underline: "Altı Çizili",
                strike: "Üstü Çizili",
                color: "Metin Rengi",
                bulletList: "Madde İşareti",
                orderedList: "Numaralı Liste",
                blockquote: "Alıntı",
                codeBlock: "Kod Bloğu",
                link: "Link",
                table: "Tablo",
                image: "Resim",
                undo: "Geri Al",
                redo: "İleri Al",
                indent: "Girinti Artır",
                outdent: "Girinti Azalt",
                alignLeft: "Sola Hizala",
                alignCenter: "Ortala",
                alignRight: "Sağa Hizala",
                alignJustify: "İki Yana Yasla",
                emoji: "Emoji Ekle",
                ai: "AI Asistanı",
            };

            this.tooltipText = tooltips[type] || "";

            const rect = event.target.getBoundingClientRect();
            const editorRect = this.$el.getBoundingClientRect();

            this.tooltipStyle = {
                top: `${rect.top - editorRect.top - 30}px`,
                left: `${rect.left + rect.width / 2 - 30}px`,
            };

            this.tooltipVisible = true;

            this.tooltipTimeout = setTimeout(() => {
                this.tooltipVisible = false;
            }, 1500);
        },
        setTextColor(color) {
            if (!this.editor) return;
            this.editor.chain().focus().setColor(color).run();
        },
        /**
         * Yazı formatını uygular (kalın, italik, altı çizili vb.)
         */
        applyFormat(type) {
            if (!this.editor) return;

            switch (type) {
                case "bold":
                    this.editor.chain().focus().toggleBold().run();
                    break;
                case "italic":
                    this.editor.chain().focus().toggleItalic().run();
                    break;
                case "underline":
                    this.editor.chain().focus().toggleUnderline().run();
                    break;
                case "link":
                    // Link işlemi özel olarak ele alınmalı
                    if (this.editor.isActive("link")) {
                        this.editor.chain().focus().unsetLink().run();
                    } else {
                        this.makeShowLinkPopover();
                    }
                    break;
                default:
                    break;
            }
        },
        /**
         * Metni hizalar (sola, orta, sağa)
         */
        setTextAlign(align) {
            if (!this.editor) return;

            switch (align) {
                case "left":
                    this.editor.chain().focus().setTextAlign("left").run();
                    break;
                case "center":
                    this.editor.chain().focus().setTextAlign("center").run();
                    break;
                case "right":
                    this.editor.chain().focus().setTextAlign("right").run();
                    break;
                default:
                    break;
            }
        },
        /**
         * Yazı boyutunu ayarlar
         */
        setFontSize(size) {
            if (!this.editor) return;

            // Boyut değerlerine göre uygun işlemi yap
            switch (size) {
                case "small":
                    this.editor.chain().focus().setFontSize("12px").run();
                    break;
                case "normal":
                    this.editor.chain().focus().setFontSize("16px").run();
                    break;
                case "large":
                    this.editor.chain().focus().setFontSize("20px").run();
                    break;
                case "huge":
                    this.editor.chain().focus().setFontSize("24px").run();
                    break;
                default:
                    break;
            }
        },
        /**
         * Editöre içerik ekler (emoji veya diğer içerikler için)
         */
        insertContent(content) {
            if (!this.editor) return;
            this.editor.chain().focus().insertContent(content).run();
        },
        /**
         * Editörün içeriğini tamamen değiştirir
         */
        setContent(content) {
            if (!this.editor) return;
            this.editor.commands.setContent(content);
        },
        addEmoji(emoji) {
            if (this.editor) {
                this.editor.chain().focus().insertContent(emoji).run();
            }
        },
        showColorPicker() {
            // Renk seçici popup'ını göster
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();
                const editorRect = this.$el.getBoundingClientRect();

                this.colorPopoverStyle = {
                    top: `${rect.bottom - editorRect.top + 10}px`,
                    left: `${rect.left - editorRect.left}px`,
                };

                this.showColorPopover = true;
            }
        },
        hideColorPopover() {
            this.showColorPopover = false;
        },
        /**
         * Editöre resim ekler
         * @param {string} url - Resim URL'i
         */
        insertImage(url) {
            if (!this.editor) return;

            this.editor.chain().focus().setImage({ src: url }).run();
        },
    },
    watch: {
        autoFocus(newValue) {
            if (newValue && this.editor) {
                this.editor.commands.focus();
            }
        },
    },
};
</script>

<style lang="scss">
/* Kare buton stili */
.square-button {
    width: 25px !important;
    height: 25px !important;
    min-width: 25px !important;
    min-height: 25px !important;
    max-width: 25px !important;
    max-height: 25px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    i.material-icons {
        font-size: 16px !important;
    }
}

/* Metin buton stili */
.text-button {
    font-size: 14px !important;
    font-weight: 600 !important;
}

/* Form buton stili */
.form-button {
    height: 28px !important;
    min-width: 60px !important;
    width: auto !important;
    font-size: 13px !important;
    padding: 0 10px !important;
}

.mail-editor {
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 4px;

    .editor-toolbar {
        background-color: #f7f7f7;
        border-bottom: 1px solid #e8e8e8;
        padding: 6px 12px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .toolbar-group {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 2px;
            width: 100%;
            position: relative;

            .format-tools {
                display: flex;
                align-items: center;
                gap: 4px;
                background-color: transparent;
                padding: 0;
                border-radius: 0;
                border: none;
                margin-left: 4px;

                .format-section {
                    display: flex;
                    flex-direction: row;
                    gap: 2px;
                }

                .divider-vertical {
                    width: 1px;
                    height: 20px;
                    background-color: #d9d9d9;
                    margin: 0 2px;
                }
            }

            .other-tools {
                display: flex;
                align-items: center;
                gap: 2px;
            }

            .toolbar-spacer {
                flex: 1;
            }

            .divider {
                width: 1px;
                height: 20px;
                background-color: #d9d9d9;
                margin: 0 2px;
            }
        }
    }

    .tiptap-editor {
        min-height: 250px;
        max-height: 450px;
        overflow-y: auto;
        padding: 12px 16px;

        .ProseMirror {
            outline: none;
            height: 100%;
            min-height: 250px;
            max-height: 450px;

            > * + * {
                margin-top: 0.75em;
            }

            ul,
            ol {
                padding: 0 1rem;
            }

            blockquote {
                border-left: 3px solid #d9d9d9;
                padding-left: 1rem;
                color: #666;
            }

            pre {
                background-color: #f8f8f8;
                border-radius: 4px;
                padding: 0.5rem;
                font-family: monospace;
            }

            img {
                max-width: 100%;
                height: auto;
            }

            table {
                border-collapse: collapse;

                th,
                td {
                    border: 1px solid #d9d9d9;
                    padding: 0.5rem;
                }
            }
        }
    }

    .floating-toolbar {
        position: absolute;
        display: flex;
        align-items: center;
        gap: 2px;
        background-color: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 3px;
        padding: 2px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 1000;
    }

    .toolbar-tooltip {
        position: absolute;
        background-color: #22272d;
        color: #fff;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        z-index: 1001;
        pointer-events: none;
    }
}

/* Link popover stilleri */
.link-popover-container {
    min-width: 280px;

    .ant-popover-inner-content {
        padding: 8px !important;
        box-shadow: none !important;
        border: 1px solid #d9d9d9 !important;
        background-color: #fff !important;
    }

    .link-form {
        padding: 0;

        .form-group {
            margin-bottom: 10px;

            &.form-actions {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                margin-bottom: 0;
            }

            .action-buttons {
                display: flex;
                gap: 8px;
            }
        }
    }
}

/* Renk seçici stilleri */
.color-picker {
    padding: 5px;

    .color-options {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 6px;

        .color-option {
            width: 28px;
            height: 28px;
            border-radius: 4px;
            border: 1px solid #d9d9d9;
            cursor: pointer;

            &:hover {
                transform: scale(1.1);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }
        }
    }
}

.color-popover {
    position: absolute;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 8px;
    z-index: 1000;
    min-width: 200px;

    .popover-title {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
        padding-bottom: 4px;
        border-bottom: 1px solid #f0f0f0;
    }

    .color-picker-container {
        .tabs {
            display: flex;
            margin-bottom: 8px;

            .tab {
                padding: 4px 8px;
                font-size: 12px;
                cursor: pointer;
                border-radius: 3px;

                &.active {
                    background-color: #f0f0f0;
                    font-weight: 500;
                }

                &:hover:not(.active) {
                    background-color: #f9f9f9;
                }
            }
        }

        .color-options {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 5px;

            .color-option {
                width: 24px;
                height: 24px;
                border-radius: 4px;
                border: 1px solid #d9d9d9;
                cursor: pointer;

                &:hover {
                    transform: scale(1.1);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }
            }
        }
    }
}
</style>
